import { ChordQuality } from './chords';
import { ChordFunction } from './keys';

export type Mode =
  | 'ionian' | 'dorian' | 'phrygian' | 'lydian'
  | 'mixolydian' | 'aeolian' | 'locrian';

// Brightness order — Lydian brightest, Locrian darkest.
// Used wherever modes are listed sequentially.
export const MODES: Mode[] = [
  'lydian', 'ionian', 'mixolydian',
  'dorian', 'aeolian', 'phrygian', 'locrian',
];

export const MODE_INTERVALS: Record<Mode, number[]> = {
  ionian:     [0, 2, 4, 5, 7, 9, 11],
  dorian:     [0, 2, 3, 5, 7, 9, 10],
  phrygian:   [0, 1, 3, 5, 7, 8, 10],
  lydian:     [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  aeolian:    [0, 2, 3, 5, 7, 8, 10],
  locrian:    [0, 1, 3, 5, 6, 8, 10],
};

export const MODE_DIATONIC_QUALITIES: Record<Mode, ChordQuality[]> = {
  ionian:     ['maj', 'min', 'min', 'maj', 'maj', 'min', 'dim'],
  dorian:     ['min', 'min', 'maj', 'maj', 'min', 'dim', 'maj'],
  phrygian:   ['min', 'maj', 'maj', 'min', 'dim', 'maj', 'min'],
  lydian:     ['maj', 'maj', 'min', 'dim', 'maj', 'min', 'min'],
  mixolydian: ['maj', 'min', 'dim', 'maj', 'min', 'min', 'maj'],
  aeolian:    ['min', 'dim', 'maj', 'min', 'min', 'maj', 'maj'],
  locrian:    ['dim', 'maj', 'min', 'min', 'maj', 'maj', 'min'],
};

export const MODE_ROMAN_NUMERALS: Record<Mode, string[]> = {
  ionian:     ['I',   'ii',  'iii',  'IV',   'V',   'vi',  'vii*'],
  dorian:     ['i',   'ii',  'bIII', 'IV',   'v',   'vi*', 'bVII'],
  phrygian:   ['i',   'bII', 'bIII', 'iv',   'v*',  'bVI', 'bvii'],
  lydian:     ['I',   'II',  'iii',  '#iv*', 'V',   'vi',  'vii'],
  mixolydian: ['I',   'ii',  'iii*', 'IV',   'v',   'vi',  'bVII'],
  aeolian:    ['i',   'ii*', 'bIII', 'iv',   'v',   'bVI', 'bVII'],
  locrian:    ['i*',  'bII', 'biii', 'iv',   'bV',  'bVI', 'bvii'],
};

export const MODE_CHORD_FUNCTIONS: Record<Mode, ChordFunction[]> = {
  ionian:     ['Tonic', 'Subdominant', 'Tonic', 'Subdominant', 'Dominant', 'Tonic', 'Leading'],
  dorian:     ['Tonic', 'Subdominant', 'Tonic', 'Subdominant', 'Dominant', 'Leading', 'Subdominant'],
  phrygian:   ['Tonic', 'Subdominant', 'Tonic', 'Subdominant', 'Leading', 'Subdominant', 'Dominant'],
  lydian:     ['Tonic', 'Dominant', 'Tonic', 'Leading', 'Dominant', 'Tonic', 'Subdominant'],
  mixolydian: ['Tonic', 'Subdominant', 'Leading', 'Subdominant', 'Dominant', 'Tonic', 'Subdominant'],
  aeolian:    ['Tonic', 'Leading', 'Tonic', 'Subdominant', 'Dominant', 'Subdominant', 'Dominant'],
  locrian:    ['Tonic', 'Subdominant', 'Tonic', 'Subdominant', 'Dominant', 'Subdominant', 'Subdominant'],
};

// Each mode's tonic is the Nth degree of its parent major scale.
// PARENT_OFFSET[mode] is the semitone distance from mode-tonic to parent-tonic.
export const PARENT_OFFSET: Record<Mode, number> = {
  ionian:      0,
  dorian:     -2,
  phrygian:   -4,
  lydian:     -5,
  mixolydian: -7,
  aeolian:    -9,
  locrian:   -11,
};

// Mode colours, ordered conceptually by brightness — bright at the top of
// the brightness ladder, progressively deeper toward Locrian. Used as the
// fill colour of each mode segment in CircleOfFifthsWheel.
export const MODE_COLOURS: Record<Mode, string> = {
  lydian:     '#FFE066', // bright yellow
  ionian:     '#FFB347', // amber gold
  mixolydian: '#FF7B54', // burnt orange
  dorian:     '#C77DFF', // soft violet
  aeolian:    '#4D96FF', // medium blue
  phrygian:   '#5B5FCC', // deep indigo
  locrian:    '#3D4154', // dark slate
};
